import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@engie-group/fluid-design-system-react/css';
import './index.css';

import Landing from './pages/Landing';
import Home from './pages/Home';
import UrbanMapHome from './pages/UrbanMapHome';
import Plan2CostHome from './pages/Plan2CostHome';
import Kits from './pages/Kits';
import KitDetail from './pages/KitDetail';
import { discoverPrototypes } from './shared/discoverPrototypes';
import { discoverKits } from './shared/discoverKits';
import { UM_REGISTRY } from './prototypes/urbanmap/_registry';
import { PrototypeNavProvider } from './shared/PrototypeNavContext';
import { OktaSecurity, LoginCallback } from './auth/OktaProvider';
import { RequireAuth } from './auth/RequireAuth';
import { isBffConfigured } from './config';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// MSW is enabled in BOTH dev AND production builds because this site is
// a prototyping studio: there is no real backend, and prototypes rely on
// in-browser mocks to render their data too.
async function enableMocking() {
  const { worker } = await import('./mocks/browser');
  return worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: {
      url: `${import.meta.env.BASE_URL}mockServiceWorker.js`,
    },
  });
}

const s2mPrototypes  = discoverPrototypes('size2mix');
const urbanPrototypes = discoverPrototypes('urbanmap');
const planPrototypes = discoverPrototypes('plan2cost');
const kits = discoverKits();

const ComponentShowcasePage = lazy(() => import('./prototypes/urbanmap/_components/ComponentShowcase'));

const ProtoFallback = () => <div style={{ padding: 40 }}>Loading…</div>;

// Prototype routes must require Okta login too: without a session the page
// renders but the BFF calls (comments, state) fail for lack of a token.
const withAuth = (element: React.ReactNode) =>
  isBffConfigured() ? <RequireAuth>{element}</RequireAuth> : element;

function S2MRoutes() {
  return (
    <PrototypeNavProvider prototypes={s2mPrototypes} context="size2mix">
      <Routes>
        <Route
          path="/"
          element={
            isBffConfigured() ? (
              <RequireAuth><Home prototypes={s2mPrototypes} /></RequireAuth>
            ) : (
              <Home prototypes={s2mPrototypes} />
            )
          }
        />
        {s2mPrototypes.map((proto) => {
          const Component = lazy(proto.loader);
          return (
            <Route
              key={proto.slug}
              path={`/prototype/${proto.slug}`}
              element={withAuth(
                <Suspense fallback={<ProtoFallback />}>
                  <Component />
                </Suspense>
              )}
            />
          );
        })}
        <Route path="*" element={<Navigate to="/size2mix" replace />} />
      </Routes>
    </PrototypeNavProvider>
  );
}

function UrbanMapRoutes() {
  const pageEntries = UM_REGISTRY.filter((e) => e.type === 'page' && e.loader);

  return (
    <PrototypeNavProvider prototypes={urbanPrototypes} context="urbanmap">
      <Routes>
        <Route
          path="/"
          element={
            isBffConfigured() ? (
              <RequireAuth><UrbanMapHome prototypes={urbanPrototypes} /></RequireAuth>
            ) : (
              <UrbanMapHome prototypes={urbanPrototypes} />
            )
          }
        />
        {urbanPrototypes.map((proto) => {
          const Component = lazy(proto.loader);
          return (
            <Route
              key={proto.slug}
              path={`/prototype/${proto.slug}`}
              element={withAuth(
                <Suspense fallback={<ProtoFallback />}>
                  <Component />
                </Suspense>
              )}
            />
          );
        })}
        {pageEntries.map((entry) => {
          const Component = lazy(entry.loader!);
          return (
            <Route
              key={entry.slug}
              path={`/page/${entry.slug}`}
              element={withAuth(
                <Suspense fallback={<ProtoFallback />}>
                  <Component />
                </Suspense>
              )}
            />
          );
        })}
        <Route
          path="/component/:slug"
          element={withAuth(
            <Suspense fallback={<ProtoFallback />}>
              <ComponentShowcasePage />
            </Suspense>
          )}
        />
        <Route path="*" element={<Navigate to="/urbanmap" replace />} />
      </Routes>
    </PrototypeNavProvider>
  );
}

function Plan2CostRoutes() {
  return (
    <PrototypeNavProvider prototypes={planPrototypes} context="plan2cost">
      <Routes>
        <Route
          path="/"
          element={
            isBffConfigured() ? (
              <RequireAuth><Plan2CostHome prototypes={planPrototypes} /></RequireAuth>
            ) : (
              <Plan2CostHome prototypes={planPrototypes} />
            )
          }
        />
        {planPrototypes.map((proto) => {
          const Component = lazy(proto.loader);
          return (
            <Route
              key={proto.slug}
              path={`/prototype/${proto.slug}`}
              element={withAuth(
                <Suspense fallback={<ProtoFallback />}>
                  <Component />
                </Suspense>
              )}
            />
          );
        })}
        <Route path="*" element={<Navigate to="/plan2cost" replace />} />
      </Routes>
    </PrototypeNavProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login/callback" element={isBffConfigured() ? <LoginCallback /> : <Navigate to="/" replace />} />
      <Route
        path="/"
        element={isBffConfigured() ? <RequireAuth><Landing /></RequireAuth> : <Landing />}
      />
      <Route path="/kits" element={isBffConfigured() ? <RequireAuth><Kits kits={kits} /></RequireAuth> : <Kits kits={kits} />} />
      <Route path="/kits/:slug" element={isBffConfigured() ? <RequireAuth><KitDetail kits={kits} /></RequireAuth> : <KitDetail kits={kits} />} />
      <Route path="/size2mix/*" element={<S2MRoutes />} />
      <Route path="/urbanmap/*" element={<UrbanMapRoutes />} />
      <Route path="/plan2cost/*" element={<Plan2CostRoutes />} />
      {/* Legacy redirect: old /prototype/:slug → /size2mix/prototype/:slug */}
      <Route path="/prototype/:slug" element={<LegacyProtoRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function LegacyProtoRedirect() {
  const slug = window.location.pathname.replace(/^\/prototype\//, '');
  return <Navigate to={`/size2mix/prototype/${slug}`} replace />;
}

enableMocking().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          {isBffConfigured() ? (
            <OktaSecurity>
              <AppRoutes />
            </OktaSecurity>
          ) : (
            <AppRoutes />
          )}
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
});
